import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  X as XMarkIcon,
  FileText as DocumentCheckIcon,
  CheckCircle,
  Clock,
  Server,
  Loader2 as LoaderIcon
} from 'lucide-react';

const Generatetest = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(true);
  const [urlId, setUrlId] = useState('');
  const [scanId, setScanId] = useState('');
  const [generating, setGenerating] = useState(false);
  const [step, setStep] = useState('input'); // input, generating, success
  const [availableScans, setAvailableScans] = useState([]);
  const [selectedScan, setSelectedScan] = useState('');
  const [generatedReport, setGeneratedReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportsHistory, setReportsHistory] = useState([]);

  // Mock data for testing when API is not available
  const mockScans = [
    {
      scan_id: 'scan-1-12345678',
      url_id: '1',
      url_name: 'https://example.com',
      is_latest: true,
      has_report: false,
      created_at: '2024-01-15T10:30:00Z'
    },
    {
      scan_id: 'scan-2-87654321',
      url_id: '2',
      url_name: 'https://test-site.org',
      is_latest: true,
      has_report: true,
      created_at: '2024-01-14T15:45:00Z'
    },
    {
      scan_id: 'scan-3-abcdef12',
      url_id: '1',
      url_name: 'https://example.com',
      is_latest: false,
      has_report: true,
      created_at: '2024-01-10T09:20:00Z'
    }
  ];

  const mockReport = {
    report_id: 'report-' + Date.now(),
    scan_id: '',
    frameworks_processed: 'WCAG 2.1, ADA, Section 508',
    overall_compliance: '87',
    generated_at: new Date().toISOString(),
    details: {
      total_checks: 156,
      passed: 136,
      failed: 20,
      warnings: 8
    }
  };

  // Fetch available scans when component mounts
  useEffect(() => {
    fetchAvailableScans();
  }, []);

  const fetchAvailableScans = async () => {
    try {
      setLoading(true);
      // Try real API first
      const response = await fetch('http://localhost:4000/api/scans');
      if (response.ok) {
        const data = await response.json();
        setAvailableScans(data.scans || []);
        toast.success('Connected to real API');
      } else {
        throw new Error('API not available');
      }
    } catch (error) {
      console.log('Using mock data:', error.message);
      // Use mock data if API is not available
      setAvailableScans(mockScans);
      toast.success('Using mock data for demonstration');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!scanId.trim()) {
      toast.error('Please select a scan from the dropdown');
      return;
    }

    setGenerating(true);
    setStep('generating');

    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Try real API first
      const endpoint = `http://localhost:4000/api/generate-report-from-scan/${scanId}`;
      const response = await fetch(endpoint, { 
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' } 
      });

      let reportData;
      
      if (response.ok) {
        reportData = await response.json();
        toast.success('Report generated via real API');
        navigate("/LandingPage");
      } else {
        // Use mock data if API fails
        reportData = {
          ...mockReport,
          scan_id: scanId,
          report_id: `report-${Date.now()}-mock`
        };
        toast.success('Report generated with mock data');
      }

      setGeneratedReport(reportData);
      setStep('success');
      
      // Add to history
      setReportsHistory(prev => [reportData, ...prev.slice(0, 4)]); // Keep last 5 reports

    } catch (error) {
      console.error('Error generating report:', error);
      // Fallback to mock data
      const mockReportData = {
        ...mockReport,
        scan_id: scanId,
        report_id: `report-${Date.now()}-mock`
      };
      setGeneratedReport(mockReportData);
      setStep('success');
      setReportsHistory(prev => [mockReportData, ...prev.slice(0, 4)]);
      toast.success('Report generated with mock data (fallback)');
    } finally {
      setGenerating(false);
    }
  };

  const handleScanSelect = (scan) => {
    setSelectedScan(`${scan.url_name} (URL ID: ${scan.url_id}) - Scan: ${scan.scan_id.substring(0, 8)}...`);
    setUrlId(scan.url_id.toString());
    setScanId(scan.scan_id);
  };

  const resetForm = () => {
    setUrlId('');
    setScanId('');
    setSelectedScan('');
    setGeneratedReport(null);
    setStep('input');
    setGenerating(false);
  };

  const handleNewReport = () => {
    resetForm();
  };

  const handleClose = () => {
    resetForm();
    setIsOpen(false);
  };

  const handleOpen = () => {
    setIsOpen(true);
    fetchAvailableScans();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Generate Report API Test Page
          </h1>
          <p className="text-lg text-gray-600">
            Test the report generation functionality with real API or mock data
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center">
              <Server className="w-8 h-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">API Status</p>
                <p className="text-2xl font-bold text-gray-900">{
                  availableScans === mockScans ? 'Mock Data' : 'Live'
                }</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center">
              <DocumentCheckIcon className="w-8 h-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Available Scans</p>
                <p className="text-2xl font-bold text-gray-900">{availableScans.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center">
              <CheckCircle className="w-8 h-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Reports Generated</p>
                <p className="text-2xl font-bold text-gray-900">{reportsHistory.length}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Report Generator */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Report Generator</h2>
              <button
                onClick={handleOpen}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Open Generator
              </button>
            </div>

            {isOpen && (
              <div className="relative bg-white rounded-2xl border border-gray-200">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <DocumentCheckIcon className="w-5 h-5 mr-2 text-blue-600" />
                    Generate Compliance Report
                  </h3>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="rounded-lg p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 transition-colors duration-200"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                {/* Content */}
                <div className="p-6">
                  {step === 'input' && (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          Select URL and Scan for Report Generation
                        </label>

                        {loading ? (
                          <div className="flex items-center justify-center py-8">
                            <LoaderIcon className="w-6 h-6 text-blue-600 animate-spin" />
                            <span className="ml-2 text-gray-600">Loading available scans...</span>
                          </div>
                        ) : availableScans.length === 0 ? (
                          <p className="text-gray-500 text-center py-8">No scans available.</p>
                        ) : (
                          <div className="space-y-3">
                            <select
                              value={selectedScan}
                              onChange={(e) => {
                                const selectedValue = e.target.value;
                                if (!selectedValue) return setSelectedScan('');
                                const scan = availableScans.find(
                                  s => `${s.url_name} (URL ID: ${s.url_id}) - Scan: ${s.scan_id.substring(0, 8)}...` === selectedValue
                                );
                                if (scan) handleScanSelect(scan);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="">Select a URL and Scan...</option>
                              {availableScans.map(scan => (
                                <option key={scan.scan_id} value={`${scan.url_name} (URL ID: ${scan.url_id}) - Scan: ${scan.scan_id.substring(0, 8)}...`}>
                                  {scan.url_name} (URL ID: {scan.url_id}) - Scan: {scan.scan_id.substring(0, 8)}... {scan.is_latest ? '(Latest)' : scan.has_report ? '(Has Report)' : '(New)'}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>

                      <div className="flex justify-end space-x-3">
                        <button type="button" onClick={handleClose} className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                        <button type="submit" disabled={!selectedScan || generating} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center">
                          <DocumentCheckIcon className="w-4 h-4 mr-2" /> Generate Report
                        </button>
                      </div>
                    </form>
                  )}

                  {step === 'generating' && (
                    <div className="text-center py-8">
                      <LoaderIcon className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
                      <p className="text-gray-600">Generating report...</p>
                    </div>
                  )}

                  {step === 'success' && generatedReport && (
                    <div className="text-center py-8">
                      <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
                      <p className="text-gray-900 font-medium mb-2">Report Generated Successfully!</p>
                      <div className="bg-gray-50 rounded-lg p-4 text-left space-y-2">
                        <p><strong>Report ID:</strong> {generatedReport.report_id}</p>
                        <p><strong>Scan ID:</strong> {generatedReport.scan_id}</p>
                        <p><strong>Frameworks:</strong> {generatedReport.frameworks_processed}</p>
                        <p><strong>Overall Compliance:</strong> {generatedReport.overall_compliance}%</p>
                      </div>
                      <button
                        onClick={handleNewReport}
                        className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                      >
                        Generate New Report
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Reports History */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Recent Reports</h2>
            
            {reportsHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <DocumentCheckIcon className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>No reports generated yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {reportsHistory.map((report, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-medium text-gray-900">Report {report.report_id}</span>
                      <span className="text-sm text-gray-500">
                        {new Date(report.generated_at || Date.now()).toLocaleTimeString()}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>Scan: {report.scan_id.substring(0, 12)}...</p>
                      <p>Compliance: <strong>{report.overall_compliance}%</strong></p>
                      <p>Frameworks: {report.frameworks_processed}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* API Information */}
        <div className="mt-8 bg-white rounded-2xl shadow-xl p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">API Endpoints</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">GET /api/scans</h3>
              <p className="text-sm text-gray-600">Fetches available scans for report generation</p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">POST /api/generate-report-from-scan/{'{scanId}'}</h3>
              <p className="text-sm text-gray-600">Generates a compliance report from a scan</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Generatetest;